import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { extractVideoId } from '../../utils/youtubeUtils';

/**
 * LectureThumbnail Component
 * Handles YouTube thumbnail loading with fallback to different resolutions
 */
const LectureThumbnail = ({ lecture, theme, videoId, formatDuration, getLectureProgress }) => {
  const [thumbnailState, setThumbnailState] = useState({
    url: null,
    attemptIndex: 0,
    failed: false
  });
  
  // Track the actually loaded URL separately
  const [loadedUrl, setLoadedUrl] = useState(null);

  // YouTube thumbnail resolutions in order of preference
  // Start with hqdefault for better compatibility
  const thumbnailResolutions = [
    'hqdefault',       // 480x360 (more commonly available)
    'maxresdefault',  // 1280x720 (not always available)
    'mqdefault',       // 320x180
    'default'          // 120x90 (always available)
  ];

  useEffect(() => {
    // Reset loaded URL when video changes
    setLoadedUrl(null);
    
    if (videoId) {
      // Start with the first resolution
      setThumbnailState({
        url: `https://img.youtube.com/vi/${videoId}/${thumbnailResolutions[0]}.jpg`,
        attemptIndex: 0,
        failed: false
      });
    } else {
      setThumbnailState({ url: null, attemptIndex: 0, failed: true });
    }
  }, [videoId]);

  const handleImageError = () => {
    const nextIndex = thumbnailState.attemptIndex + 1;
    
    if (nextIndex < thumbnailResolutions.length) {
      // Try next resolution
      setThumbnailState({
        url: `https://img.youtube.com/vi/${videoId}/${thumbnailResolutions[nextIndex]}.jpg`,
        attemptIndex: nextIndex,
        failed: false
      });
    } else {
      // All attempts failed
      setThumbnailState({
        ...thumbnailState,
        failed: true
      });
      // Keep loadedUrl as null so placeholder stays visible
      setLoadedUrl(null);
    }
  };
  
  const handleImageLoad = (e) => {
    // Image loaded successfully, save the URL
    setLoadedUrl(e.currentTarget.src);
  };

  // Generate placeholder gradient
  const getPlaceholderGradient = () => {
    // Handle missing or invalid lecture IDs
    let lectureNumber = lecture.lectureIdInSeries || lecture.lectureId;
    
    // Convert string IDs to numbers or generate hash
    if (lectureNumber !== undefined && lectureNumber !== null) {
      // Try to parse as number
      const parsed = parseInt(lectureNumber, 10);
      if (!isNaN(parsed)) {
        lectureNumber = parsed;
      } else if (typeof lectureNumber === 'string') {
        // Generate a hash from string to get consistent color
        let hash = 0;
        for (let i = 0; i < lectureNumber.length; i++) {
          hash = ((hash << 5) - hash) + lectureNumber.charCodeAt(i);
          hash = hash & hash; // Convert to 32-bit integer
        }
        lectureNumber = Math.abs(hash);
      }
    }
    
    // Fallback: use title hash if no ID available
    if (!lectureNumber && lecture.title) {
      let hash = 0;
      const title = lecture.title || 'Lecture';
      for (let i = 0; i < title.length; i++) {
        hash = ((hash << 5) - hash) + title.charCodeAt(i);
        hash = hash & hash;
      }
      lectureNumber = Math.abs(hash);
    }
    
    // Final fallback
    if (!lectureNumber) {
      lectureNumber = 1;
    }
    
    const gradients = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      'linear-gradient(135deg, #30cfd0 0%, #330867 100%)'
    ];
    
    const index = Math.abs(lectureNumber - 1) % gradients.length;
    return gradients[index];
  };

  // Only show placeholder when all thumbnail attempts have failed
  const shouldShowPlaceholder = thumbnailState.failed && !loadedUrl;
  const placeholderGradient = getPlaceholderGradient();

  return (
    <div style={{
      position: 'relative',
      paddingTop: '56.25%',
      backgroundColor: shouldShowPlaceholder ? '#ffffff' : '#f3f4f6',  // White for placeholder, light gray while loading
      overflow: 'hidden',
      transition: 'background-color 0.3s ease'
    }}>
      {/* Hidden image to test loading */}
      {thumbnailState.url && !thumbnailState.failed && (
        <img
          key={`thumb-${videoId}-${thumbnailState.attemptIndex}`}
          src={thumbnailState.url}
          onError={handleImageError}
          onLoad={handleImageLoad}
          style={{ display: 'none' }}
          alt=""
          crossOrigin="anonymous"
        />
      )}
      
      {/* Actual thumbnail div - only render when image is loaded */}
      {loadedUrl && (
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${loadedUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            zIndex: 1
          }}
        />
      )}
      
      {/* Simple placeholder for missing thumbnails - only show when failed */}
      {shouldShowPlaceholder && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2
        }}>
          {/* Microphone Icon with theme color */}
          <svg 
            width="48" 
            height="48" 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path 
              d="M12 1C10.34 1 9 2.34 9 4V12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12V4C15 2.34 13.66 1 12 1Z" 
              fill={theme?.colors?.primary || '#0f172a'}
            />
            <path 
              d="M17 11C17 11 17 12 17 12C17 14.76 14.76 17 12 17C9.24 17 7 14.76 7 12V11H5V12C5 15.64 7.81 18.64 11.28 18.97V22H12.72V18.97C16.19 18.64 19 15.64 19 12V11H17Z" 
              fill={theme?.colors?.primary || '#0f172a'}
            />
          </svg>
        </div>
      )}
      
      {/* Duration overlay */}
      {lecture.duration && (
        <div style={{
          position: 'absolute',
          bottom: '8px',
          right: '8px',
          backgroundColor: 'rgba(0,0,0,0.8)',
          color: 'white',
          padding: '2px 6px',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: '500'
        }}>
          {formatDuration(lecture.duration)}
        </div>
      )}
      
      {/* Progress bar */}
      {getLectureProgress && getLectureProgress(lecture.lectureId) > 0 && (
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '3px',
          backgroundColor: 'rgba(0,0,0,0.3)'
        }}>
          <div style={{
            width: `${getLectureProgress(lecture.lectureId)}%`,
            height: '100%',
            backgroundColor: theme?.colors?.secondary || '#3b82f6'
          }} />
        </div>
      )}
    </div>
  );
};

/**
 * Lecture Series Grid Component
 * Displays current lecture series in a grid layout with continue watching
 */
const LectureSeriesGrid = ({ 
  lectures = [], 
  loading, 
  theme, 
  isMobile,
  onLectureWatch
}) => {
  const [lastWatched, setLastWatched] = useState(null);
  const [viewedLectures, setViewedLectures] = useState([]);

  // Load personalization data from localStorage
  useEffect(() => {
    try {
      const watched = localStorage.getItem('lastWatchedLecture');
      if (watched) {
        setLastWatched(JSON.parse(watched));
      }
      
      const viewed = localStorage.getItem('viewedLectures');
      if (viewed) {
        setViewedLectures(JSON.parse(viewed));
      }
    } catch (e) {
      console.error('Error loading watched data:', e);
    }
  }, []);

  // Get display lectures with smart daily rotation
  const getDisplayLectures = () => {
    if (!lectures || lectures.length === 0) return [];
    
    // Calculate day of year for daily rotation
    const today = new Date();
    const start = new Date(today.getFullYear(), 0, 0);
    const dayOfYear = Math.floor((today - start + ((start.getTimezoneOffset() - today.getTimezoneOffset()) * 60 * 1000)) / 86400000);
    
    // Sort lectures by date (newest first)
    const sorted = [...lectures].sort((a, b) => {
      const dateA = new Date(a.date || a.created_at);
      const dateB = new Date(b.date || b.created_at);
      return dateB - dateA;
    });
    
    // If we have 4 or fewer lectures, just show them all
    if (sorted.length <= 4) {
      return sorted;
    }
    
    // Strategy: Show 2 newest + 2 rotating older lectures
    const newestLectures = sorted.slice(0, 2);  // Always show 2 most recent
    const olderLectures = sorted.slice(2);      // Pool of older lectures to rotate through
    
    // Calculate which older lectures to show today
    // Each day, shift by 2 positions through the older lectures
    const rotationIndex = (dayOfYear * 2) % olderLectures.length;
    
    // Get 2 older lectures based on today's rotation
    const rotatedOlder = [];
    if (olderLectures.length > 0) {
      rotatedOlder.push(olderLectures[rotationIndex % olderLectures.length]);
      if (olderLectures.length > 1) {
        rotatedOlder.push(olderLectures[(rotationIndex + 1) % olderLectures.length]);
      }
    }
    
    // Combine: 2 newest + 2 rotating older (max 4 total)
    const displayLectures = [...newestLectures, ...rotatedOlder].slice(0, 4);
    
    return displayLectures;
  };

  // Calculate progress for a lecture
  const getLectureProgress = (lectureId) => {
    if (!lastWatched || lastWatched.lectureId !== lectureId) return 0;
    if (!lastWatched.duration || !lastWatched.timestamp) return 0;
    return Math.min(100, (lastWatched.timestamp / lastWatched.duration) * 100);
  };

  // Check if lecture is new (within last 7 days)
  const isNewLecture = (lecture) => {
    const date = new Date(lecture.date || lecture.created_at);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return date > weekAgo && !viewedLectures.includes(lecture.lectureId);
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes} min`;
  };

  // Handle lecture click
  const handleLectureClick = (lecture) => {
    if (onLectureWatch) {
      onLectureWatch(lecture.lectureId, 0, lecture.duration);
    }
    
    // Mark as viewed
    try {
      const viewed = JSON.parse(localStorage.getItem('viewedLectures') || '[]');
      if (!viewed.includes(lecture.lectureId)) {
        viewed.push(lecture.lectureId);
        localStorage.setItem('viewedLectures', JSON.stringify(viewed));
      }
    } catch (e) {
      console.error('Error saving viewed lecture:', e);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={{ marginTop: '0', marginBottom: '52px' }}>
        <h2 style={{
          fontSize: isMobile ? '20px' : '24px',
          fontWeight: '600',
          marginBottom: '20px',
          color: theme?.colors?.primary || '#0f172a'
        }}>
          Latest Lecture Series
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: '16px'
        }}>
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} style={{
              height: '240px',
              backgroundColor: theme?.colors?.lightGray || '#f1f5f9',
              borderRadius: '8px',
              animation: 'pulse 1.5s ease-in-out infinite'
            }} />
          ))}
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </div>
    );
  }

  const displayLectures = getDisplayLectures();
  
  // No lectures
  if (!displayLectures || displayLectures.length === 0) {
    return null;
  }

  // No need for sorting again or continue watching

  return (
    <div style={{ marginTop: '0', marginBottom: '52px' }}>
      {/* Section Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '8px'
      }}>
        <div>
          <h2 style={{
            fontSize: isMobile ? '20px' : '24px',
            fontWeight: '600',
            color: theme?.colors?.headingColor || theme?.colors?.primary || '#0f172a',
            margin: 0
          }}>
            Detailed Lecture Notes
          </h2>
          <p style={{
            margin: '2px 0 0 0',
            fontSize: '13px',
            color: theme?.colors?.subtitleColor || theme?.colors?.textSecondary || '#64748b'
          }}>
            Recommended for you
          </p>
        </div>
        <Link
          to="/lectures"
          style={{
            color: theme?.colors?.linkColor || theme?.colors?.primary || '#0f172a',
            textDecoration: 'none',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'color 0.2s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = theme?.colors?.secondary || '#3b82f6';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = theme?.colors?.linkColor || theme?.colors?.primary || '#0f172a';
          }}
        >
          View All →
        </Link>
      </div>

      {/* Lectures Grid - 2 columns on mobile, 4 on desktop */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: isMobile ? '12px' : '16px'
        // Cards will automatically stretch to match row height
      }}>
        {displayLectures.map((lecture, index) => {
          const progress = getLectureProgress(lecture.lectureId);
          const isNew = isNewLecture(lecture);
          const lectureNumber = lecture.lectureIdInSeries || lecture.lectureId || index + 1;
          
          
          return (
            <Link
              key={lecture.lectureId}
              to={`/lecture/${lecture.lectureId}`}
              onClick={(e) => {
                // Don't prevent default - let the Link navigate
                handleLectureClick(lecture);
              }}
              style={{
                textDecoration: 'none',
                color: 'inherit',
                display: 'block'
              }}
            >
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                cursor: 'pointer',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.05)';
              }}
              >
                {/* New badge at card level */}
                {isNew && (
                  <div style={{
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    backgroundColor: theme?.colors?.accent || '#ef4444',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '0 12px 0 8px',
                    fontSize: '10px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    zIndex: 2
                  }}>
                    New
                  </div>
                )}
                {/* Blue Header with Lecture Number */}
                <div style={{
                  backgroundColor: theme?.colors?.primary || '#0f172a',
                  padding: isMobile ? '16px' : '20px',
                  textAlign: 'center',
                  color: 'white',
                  borderRadius: '8px 8px 0 0',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px'
                }}>
                  <div style={{
                    fontSize: isMobile ? '18px' : '20px',
                    fontWeight: 'bold',
                    lineHeight: 1
                  }}>
                    Lecture {lectureNumber}
                  </div>
                  
                  {/* Series/Event Subtitle */}
                  {(() => {
                    // Function to clean up series names by removing years
                    const cleanSeriesName = (name) => {
                      if (!name) return name;
                      // Remove patterns like "Ramadan 2025 - " or just "2025 - "
                      return name
                        .replace(/^(Ramadan|Muharram|Eid)?\s*\d{4}\s*-\s*/, '')  // Remove "Event 2024 - " from start
                        .replace(/\s*-\s*(Ramadan|Muharram|Eid)?\s*\d{4}/, '')  // Remove " - Event 2024" patterns
                        .replace(/^\d{4}\s*-\s*/, '')  // Remove just "2024 - " from start
                        .replace(/\s+\d{4}$/, '')  // Remove " 2024" from end
                        .trim();
                    };
                    
                    const getSubtitle = () => {
                      // Priority: series > event > speaker
                      if (lecture.series && lecture.series !== 'Uncategorized') {
                        return cleanSeriesName(lecture.series);
                      }
                      if (lecture.event && lecture.event !== 'Uncategorized') {
                        return cleanSeriesName(lecture.event);
                      }
                      if (lecture.speaker) {
                        return `by ${lecture.speaker}`;
                      }
                      return null;
                    };
                    
                    const subtitle = getSubtitle();
                    
                    if (!subtitle) return null;
                    
                    // Truncate long subtitles
                    const displaySubtitle = subtitle.length > 25 
                      ? subtitle.substring(0, 22) + '...'
                      : subtitle;
                    
                    return (
                      <div style={{
                        fontSize: isMobile ? '12px' : '14px',
                        fontWeight: '400',
                        opacity: 0.9,
                        lineHeight: 1.2,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {displaySubtitle}
                      </div>
                    );
                  })()}
                </div>

                {/* Thumbnail or Placeholder */}
                <LectureThumbnail 
                  lecture={lecture}
                  theme={theme}
                  videoId={extractVideoId(lecture.fullVideoUrl || lecture.summaryVideoUrl)}
                  formatDuration={formatDuration}
                  getLectureProgress={getLectureProgress}
                />

                {/* Grey Box with Lecture Info */}
                <div style={{
                  backgroundColor: theme?.colors?.cardInfoBg || '#f0ece4',
                  background: `linear-gradient(to bottom, ${theme?.colors?.cardInfoBg || '#f0ece4'} 0%, ${theme?.colors?.cardInfoBgEnd || '#e8e3d9'} 100%)`,
                  padding: isMobile ? '16px' : '20px',
                  textAlign: 'center',
                  minHeight: isMobile ? '100px' : '120px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flex: 1
                }}>
                  {/* Title */}
                  <h3 style={{
                    margin: '0 0 8px 0',
                    fontSize: isMobile ? '14px' : '16px',
                    fontWeight: 'bold',
                    color: theme?.colors?.primary || '#0f172a',
                    lineHeight: 1.3,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {lecture.title}
                  </h3>
                  
                  {/* Date and Speaker */}
                  {lecture.date && (
                    <div style={{
                      fontSize: isMobile ? '11px' : '12px',
                      color: '#6c757d',
                      marginTop: 'auto'
                    }}>
                      {new Date(lecture.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                      {lecture.speaker && (
                        <>
                          <span style={{ margin: '0 4px' }}>•</span>
                          <span>{lecture.speaker}</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>

    </div>
  );
};

export default LectureSeriesGrid;